import { useState, useCallback, useRef } from 'react';
import { useWebSpeechRecording } from './useWebSpeechRecording';
import { supabase } from '@/integrations/supabase/client';

interface UnifiedRecordingOptions {
  language: string;
  bookingId: string;
  practitionerId: string;
  clientId: string;
}

interface BatchTranscriptionResult {
  transcript: string;
  rawText: string;
  utterances: Array<{
    speaker: string;
    text: string;
    start: number;
    end: number;
    confidence: number;
  }>;
  audioDuration: number;
  confidence: number;
}

export const useUnifiedRecording = (options: UnifiedRecordingOptions) => {
  const { language } = options;

  // Web Speech for real-time preview (free)
  const webSpeechLang = language.startsWith('zh') ? 'zh-CN' : 'en-US';
  const webSpeech = useWebSpeechRecording({
    language: webSpeechLang,
    continuous: true,
    interimResults: true,
  });

  // MediaRecorder for capturing audio file
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);

  // Batch transcription state
  const [isBatchProcessing, setIsBatchProcessing] = useState(false);
  const [batchTranscript, setBatchTranscript] = useState<string>('');
  const [batchError, setBatchError] = useState<string | null>(null);
  const [batchResult, setBatchResult] = useState<BatchTranscriptionResult | null>(null);

  const getSpeakers = (lang: string) => {
    if (lang.startsWith('zh')) return ['疗愈师', '客户'];
    return ['Therapist', 'Client'];
  };

  const startRecording = useCallback(async () => {
    try {
      setBatchTranscript('');
      setBatchError(null);
      setBatchResult(null);
      audioChunksRef.current = [];

      // Start MediaRecorder for audio capture
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
          ? 'audio/webm;codecs=opus'
          : 'audio/webm',
      });

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start(1000); // collect chunks every second

      // Start Web Speech for real-time preview
      webSpeech.startRecording();
    } catch (err) {
      console.error('Failed to start recording:', err);
    }
  }, [webSpeech]);

  const stopRecording = useCallback(async () => {
    // Stop Web Speech
    webSpeech.stopRecording();

    // Stop MediaRecorder and collect audio
    return new Promise<void>((resolve) => {
      const recorder = mediaRecorderRef.current;
      if (!recorder || recorder.state === 'inactive') {
        resolve();
        return;
      }

      recorder.onstop = async () => {
        // Stop the stream tracks
        streamRef.current?.getTracks().forEach(t => t.stop());
        streamRef.current = null;

        // Send audio for batch transcription
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        if (audioBlob.size > 0) {
          await sendForBatchTranscription(audioBlob);
        }
        resolve();
      };

      recorder.stop();
    });
  }, [webSpeech, language]);

  const sendForBatchTranscription = async (audioBlob: Blob) => {
    setIsBatchProcessing(true);
    setBatchError(null);

    try {
      console.log('📤 Sending audio for batch transcription...', {
        size: `${(audioBlob.size / 1024).toFixed(1)}KB`,
        type: audioBlob.type,
      });

      // Convert blob to base64
      const arrayBuffer = await audioBlob.arrayBuffer();
      const bytes = new Uint8Array(arrayBuffer);
      let binary = '';
      for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i]);
      }
      const base64 = btoa(binary);

      const assemblyLang = language.startsWith('zh') ? 'zh' : 'en';

      const { data, error } = await supabase.functions.invoke('batch-transcribe', {
        body: {
          audioBase64: base64,
          language: assemblyLang,
          speakers: getSpeakers(language),
        },
      });

      if (error) throw error;

      if (data?.success) {
        console.log('✅ Batch transcription completed');
        setBatchTranscript(data.transcript);
        setBatchResult(data);
      } else {
        throw new Error(data?.error || 'Batch transcription failed');
      }
    } catch (err: any) {
      console.error('❌ Batch transcription error:', err);
      setBatchError(err.message || 'Batch transcription failed');
    } finally {
      setIsBatchProcessing(false);
    }
  };

  const resetTranscript = useCallback(() => {
    webSpeech.resetTranscript();
    setBatchTranscript('');
    setBatchError(null);
    setBatchResult(null);
  }, [webSpeech]);

  const resetSession = useCallback(() => {
    resetTranscript();
    audioChunksRef.current = [];
  }, [resetTranscript]);

  // The final transcript is the batch result if available, otherwise the Web Speech preview
  const finalTranscript = batchTranscript || webSpeech.transcript;

  return {
    // State
    isRecording: webSpeech.isRecording,
    isProcessing: isBatchProcessing,
    error: webSpeech.error || batchError,
    isSupported: webSpeech.isSupported,

    // Web Speech real-time preview
    transcript: finalTranscript,
    interimTranscript: webSpeech.interimTranscript,
    confidence: webSpeech.confidence,
    previewTranscript: webSpeech.transcript, // always the Web Speech preview

    // Batch transcription results
    batchTranscript,
    batchResult,
    isBatchProcessing,
    batchError,

    // Engine info
    recordingEngine: 'webspeech' as const,
    hasAdvancedFeatures: false,

    // Methods
    startRecording,
    stopRecording,
    resetTranscript,
    resetSession,
  };
};
